/**
 * Environment Configuration
 *
 * Single source of truth for all environment variables.
 * Validates required vars at module load time so misconfiguration
 * is caught immediately at startup, not silently at runtime.
 *
 * Usage:
 *   import { ENV } from '@shared/config/env';
 *   const url = ENV.SUPABASE.URL;
 *   const fnUrl = ENV.SUPABASE.edgeFn('audit-create-draft');
 */

// ── Raw access helper ─────────────────────────────────────────────────────────
const get = (key) => import.meta.env[key];

// ── Supabase ──────────────────────────────────────────────────────────────────
const SUPABASE = {
  URL:      get('VITE_SUPABASE_URL')      || '',
  ANON_KEY: get('VITE_SUPABASE_ANON_KEY') || '',

  /**
   * Build a fully-qualified Edge Function URL.
   * @param {string} name  e.g. 'audit-create-draft'
   * @returns {string}     e.g. 'https://xxx.supabase.co/functions/v1/audit-create-draft'
   */
  edgeFn: (name) => `${get('VITE_SUPABASE_URL')}/functions/v1/${name}`,
};

// ── App metadata ──────────────────────────────────────────────────────────────
const APP = {
  NAME:        get('VITE_APP_NAME')    || 'Depot Induction Management System',
  VERSION:     get('VITE_APP_VERSION') || '1.0.0',
  IS_DEV:      import.meta.env.DEV,
  IS_PROD:     import.meta.env.PROD,
  MODE:        import.meta.env.MODE,   // 'development' | 'production' | 'test'
};

// ── Error tracking ────────────────────────────────────────────────────────────
const SENTRY = {
  DSN:              get('VITE_SENTRY_DSN')              || '',
  ENVIRONMENT:      get('VITE_SENTRY_ENVIRONMENT')      || import.meta.env.MODE,
  TRACES_SAMPLE_RATE: parseFloat(get('VITE_SENTRY_TRACES_SAMPLE_RATE')) || 0.1,
  ENABLED:          !!get('VITE_SENTRY_DSN'),
};

// ── AI features ───────────────────────────────────────────────────────────────
const AI = {
  ANTHROPIC_API_KEY: get('VITE_ANTHROPIC_API_KEY') || '',
  ENABLED:           !!get('VITE_ANTHROPIC_API_KEY'),
};

// ── Feature flags (BFF routing) ───────────────────────────────────────────────
const FEATURES = {
  USE_ADMIN_BFF:  get('VITE_USE_ADMIN_BFF')  === 'true',
  USE_KIOSK_BFF:  get('VITE_USE_KIOSK_BFF')  === 'true',
  USE_PORTAL_BFF: get('VITE_USE_PORTAL_BFF') === 'true',
};

// ── Startup validation ────────────────────────────────────────────────────────
/**
 * Call once in main.jsx (or App) to fail fast if required config is missing.
 * Only runs in development — production silently skips to avoid DoS from
 * misconfigured deploys exposing internals in the console.
 */
export function validateEnv() {
  if (!import.meta.env.DEV) return;

  const required = [
    ['VITE_SUPABASE_URL',      SUPABASE.URL],
    ['VITE_SUPABASE_ANON_KEY', SUPABASE.ANON_KEY],
  ];

  const missing = required.filter(([, val]) => !val).map(([key]) => key);

  if (missing.length > 0) {
    console.warn(
      `[ENV] Missing required environment variables:\n` +
      missing.map(k => `  • ${k}`).join('\n') +
      `\n\nCopy .env.example to .env.local and fill in the values.`
    );
  }

  const configured = required.filter(([, val]) => !!val).map(([key]) => key);
  if (configured.length === required.length) {
    console.info('[ENV] ✓ All required environment variables are set.');
  }
}

// ── Exports ───────────────────────────────────────────────────────────────────
export const ENV = {
  SUPABASE,
  APP,
  SENTRY,
  AI,
  FEATURES,
};
